import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ExternalLink, RefreshCw } from "lucide-react";
import { getPattern, startPatternExplore, getPatternExploration, MARKETS } from "@/lib/apiClient";
import { useSite } from "@/context/SiteContext";

const STAGE_ORDER = ["understanding", "searching", "checking", "reviewing", "preparing", "done"];
const STATE_STYLE = {
  STRONG: "bg-brlime text-ink", SOME: "bg-bryellow text-ink",
  LIMITED: "bg-brorange text-white", INSUFFICIENT: "bg-secondary text-ink/70",
};

export default function PatternExplore() {
  const { slug } = useParams();
  const { market } = useSite();
  const [pattern, setPattern] = useState(null);
  const [country, setCountry] = useState(market?.name || "South Africa");
  const [exp, setExp] = useState(null);
  const timer = useRef(null);

  useEffect(() => { getPattern(slug).then(setPattern).catch(() => {}); }, [slug]);

  const begin = useCallback((ctry) => {
    if (timer.current) clearInterval(timer.current);
    setExp(null);
    startPatternExplore(slug, ctry).then((e) => {
      setExp(e);
      if (e.status !== "done" && e.status !== "error") {
        timer.current = setInterval(() => {
          getPatternExploration(e.id).then((u) => {
            setExp(u);
            if (u.status === "done" || u.status === "error") clearInterval(timer.current);
          });
        }, 2500);
      }
    });
  }, [slug]);

  useEffect(() => { begin(country); return () => timer.current && clearInterval(timer.current); }, [slug]);

  const changeCountry = (name) => { setCountry(name); begin(name); };
  const stageIdx = STAGE_ORDER.indexOf(exp?.stage || "understanding");
  const done = exp?.status === "done";

  const grouped = {};
  (exp?.findings || []).forEach((f) => { (grouped[f.classification] = grouped[f.classification] || []).push(f); });

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <Link to={`/pattern/${slug}`} className="group inline-flex items-center gap-2 font-semibold text-sm text-ink/70"><ArrowLeft size={16} className="transition-transform group-hover:-translate-x-1" /> Back to pattern</Link>
      <p className="eyebrow text-ink/60 mt-4">Explore pattern in market</p>
      <h1 data-testid="pattern-explore-title" className="font-display font-extrabold text-3xl sm:text-4xl display-tight uppercase mt-2">{pattern?.name || "…"}</h1>

      <div className="flex flex-wrap items-center gap-3 mt-5">
        <span className="text-sm text-ink/60">Market:</span>
        <select data-testid="pattern-explore-market" value={country} onChange={(e) => changeCountry(e.target.value)} className="h-10 px-3 border-2 border-ink bg-white font-semibold text-sm">
          {MARKETS.map((m) => <option key={m.code} value={m.name}>{m.flag} {m.name}</option>)}
        </select>
        {done && <button data-testid="pattern-explore-refresh" onClick={() => begin(country)} className="btn-tactile inline-flex items-center gap-2 border-2 border-ink px-3 h-10 text-sm font-semibold hover:bg-bryellow"><RefreshCw size={14} /> Refresh</button>}
      </div>

      {exp?.concept && <p className="mt-6 text-ink/70"><span className="eyebrow text-ink/50">What we're searching for:</span><br />{exp.concept}</p>}

      {!done && exp?.status !== "error" && (
        <div className="mt-8 border-2 border-ink p-6" data-testid="pattern-explore-progress">
          <p className="font-semibold">Searching {country} for real evidence…</p>
          <div className="flex flex-wrap gap-2 mt-4">
            {STAGE_ORDER.slice(0, 5).map((s, i) => (
              <span key={s} className={`eyebrow px-2 py-1 ${i <= stageIdx ? "bg-ink text-white" : "bg-secondary text-ink/50"}`}>{s}</span>
            ))}
          </div>
        </div>
      )}

      {exp?.status === "error" && <div className="mt-8 border-2 border-brorange p-6" data-testid="pattern-explore-error">Something went wrong searching. Try Refresh.</div>}

      {done && (
        <div className="mt-8 space-y-8" data-testid="pattern-explore-results">
          <div className="flex flex-wrap items-center gap-4">
            <span className={`eyebrow px-3 py-1.5 ${STATE_STYLE[exp.result_state] || "bg-secondary"}`}>{exp.result_state} evidence</span>
            <span className="text-sm text-ink/50">{exp.searches_performed} searches · {exp.pages_reviewed} pages reviewed · {exp.relevant_found} relevant</span>
          </div>
          {exp.summary && <p className="text-lg leading-relaxed">{exp.summary}</p>}

          {Object.entries(grouped).map(([cls, items]) => (
            <div key={cls}>
              <h3 className="font-display font-extrabold text-xl uppercase tracking-tight mb-3">{cls}</h3>
              <div className="space-y-3">
                {items.map((f, i) => (
                  <a key={i} href={f.source_url} target="_blank" rel="noreferrer" className="group block border-2 border-ink p-4 hover:shadow-[4px_4px_0_0_#0A0A0C] bg-white">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <div className="font-bold">{f.title}</div>
                        <p className="text-sm text-ink/70 mt-1">{f.note || f.snippet}</p>
                      </div>
                      <ExternalLink size={16} className="shrink-0 mt-1" />
                    </div>
                  </a>
                ))}
              </div>
            </div>
          ))}

          {exp.summary === "" && Object.keys(grouped).length === 0 && <p className="text-ink/60">No clear local evidence found yet for this pattern in {country}.</p>}
        </div>
      )}
    </div>
  );
}
